import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    Select,
    MenuItem,
    Button,
    Box,
    Typography,
    Avatar,
    IconButton,
    Tooltip
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getAllInkColors } from './InkColorIcon';

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'white',
        '& fieldset': { borderColor: 'rgba(76, 29, 149, 0.5)' },
        '&:hover fieldset': { borderColor: 'rgba(76, 29, 149, 0.8)' },
        '&.Mui-focused fieldset': { borderColor: '#7c3aed' }
    },
    '& .MuiInputLabel-root': { color: 'rgba(196, 181, 253, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#7c3aed' }
};

const DeckForm = ({
    open,
    onClose,
    onSubmit,
    editingDeck,
    archetypes,
    formData,
    onInputChange,
    trackerContext,
    suggestingArchetype = false,
    archetypeSuggestions = [],
    onSuggestArchetype,
    onApplySuggestion,
    suggestingName = false,
    nameSuggestions = [],
    onSuggestName,
    onApplyNameSuggestion
}) => {
    const inkColors = getAllInkColors();

    const getInkColor = (color) => {
        const colorData = inkColors.find(c => c.value === color);
        return colorData?.color || '#666';
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    background: 'rgba(17, 24, 39, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.3)',
                    borderRadius: '20px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)'
                }
            }}
        >
            <DialogTitle sx={{
                color: 'white',
                fontWeight: 600,
                borderBottom: '1px solid rgba(76, 29, 149, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <span>{editingDeck ? 'Edytuj Talię' : 'Dodaj Nową Talię'}</span>
                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontWeight: 400 }}>
                    {trackerContext?.formatLabel || 'Core Constructed'} · {trackerContext?.setLabel || trackerContext?.setId || '-'}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

                    {/* 1. Lista kart — główny input */}
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        label="Lista kart *"
                        value={formData.cards}
                        onChange={onInputChange('cards')}
                        placeholder="Wklej listę kart z Dreamborn lub wpisz ręcznie..."
                        sx={inputSx}
                    />

                    {/* 2. Sugestia archetypu */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={onSuggestArchetype}
                            disabled={!formData.cards?.trim() || !onSuggestArchetype || suggestingArchetype}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            {suggestingArchetype ? 'Liczę sugestie...' : 'Zaproponuj archetyp'}
                        </Button>
                        {archetypeSuggestions.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {archetypeSuggestions.slice(0, 3).map((suggestion, index) => (
                                    <Button
                                        key={`${suggestion?.archetype?.id || 'unknown'}-${index}`}
                                        variant={formData.archetype === suggestion?.archetype?.id ? 'contained' : 'outlined'}
                                        onClick={() => onApplySuggestion(suggestion)}
                                        sx={{
                                            justifyContent: 'space-between',
                                            textTransform: 'none',
                                            borderColor: 'rgba(76, 29, 149, 0.5)',
                                            color: 'white'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontWeight: 600 }}>{suggestion?.archetype?.name || 'Nieznany archetyp'}</Typography>
                                            <Box sx={{ display: 'flex', gap: 0.4 }}>
                                                {(suggestion?.archetype?.ink_colors || []).map((color, colorIndex) => {
                                                    const colorData = inkColors.find(c => c.value === color);
                                                    return (
                                                        <Avatar
                                                            key={`${color}-${colorIndex}`}
                                                            src={colorData?.icon}
                                                            sx={{
                                                                width: 14,
                                                                height: 14,
                                                                backgroundColor: getInkColor(color),
                                                                border: `1px solid ${getInkColor(color)}`
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.85)', fontWeight: 600 }}>
                                            {Math.round((suggestion?.confidence || 0) * 100)}%
                                        </Typography>
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* 3. Wybór archetypu */}
                    <FormControl fullWidth>
                        <Select
                            value={formData.archetype}
                            onChange={onInputChange('archetype')}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected) {
                                    return (
                                        <Typography sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>
                                            Wybierz archetyp...
                                        </Typography>
                                    );
                                }

                                const selectedArchetype = archetypes.find((entry) => entry.id === selected);
                                if (!selectedArchetype) return selected;

                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        {selectedArchetype.ink_colors && selectedArchetype.ink_colors.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                {selectedArchetype.ink_colors.map((color, index) => {
                                                    const colorData = inkColors.find(c => c.value === color);
                                                    return (
                                                        <Avatar
                                                            key={index}
                                                            src={colorData?.icon}
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                backgroundColor: getInkColor(color),
                                                                border: `1px solid ${getInkColor(color)}`
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                        <Typography sx={{ color: 'white' }}>
                                            {selectedArchetype.name}
                                        </Typography>
                                    </Box>
                                );
                            }}
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.5)' },
                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 29, 149, 0.8)' },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#7c3aed' }
                            }}
                        >
                            <MenuItem value="" disabled>
                                <Typography sx={{ color: 'rgba(196, 181, 253, 0.5)' }}>
                                    Wybierz archetyp...
                                </Typography>
                            </MenuItem>
                            {archetypes.map((archetype) => (
                                <MenuItem key={archetype.id} value={archetype.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        {archetype.ink_colors && archetype.ink_colors.length > 0 && (
                                            <Box sx={{ display: 'flex', gap: 0.3 }}>
                                                {archetype.ink_colors.map((color, index) => {
                                                    const colorData = inkColors.find(c => c.value === color);
                                                    return (
                                                        <Avatar
                                                            key={index}
                                                            src={colorData?.icon}
                                                            sx={{
                                                                width: 16,
                                                                height: 16,
                                                                backgroundColor: getInkColor(color),
                                                                border: `1px solid ${getInkColor(color)}`
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                        <Typography sx={{ color: 'white', flex: 1 }}>
                                            {archetype.name}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* 4. Nazwa talii + sugestie nazwy */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                                fullWidth
                                label="Nazwa talii *"
                                value={formData.name}
                                onChange={onInputChange('name')}
                                sx={inputSx}
                            />
                            {formData.archetype && formData.cards?.trim() && onSuggestName && (
                                <Tooltip title="Odśwież sugestie nazwy">
                                    <IconButton
                                        onClick={onSuggestName}
                                        disabled={suggestingName}
                                        size="small"
                                        sx={{ color: 'rgba(196, 181, 253, 0.6)', flexShrink: 0 }}
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                        {nameSuggestions.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.55)', pl: 0.5 }}>
                                    Sugerowane nazwy (kliknij aby zastosować):
                                </Typography>
                                {nameSuggestions.map((suggestion, index) => (
                                    <Button
                                        key={`${suggestion}-${index}`}
                                        variant={formData.name === suggestion ? 'contained' : 'outlined'}
                                        onClick={() => onApplyNameSuggestion(suggestion)}
                                        sx={{
                                            justifyContent: 'flex-start',
                                            textTransform: 'none',
                                            borderColor: 'rgba(76, 29, 149, 0.4)',
                                            color: 'rgba(196, 181, 253, 0.85)',
                                            py: 0.75,
                                            fontSize: '0.85rem'
                                        }}
                                    >
                                        {suggestion}
                                    </Button>
                                ))}
                            </Box>
                        )}
                    </Box>

                    {/* 5. URL Dreamborn */}
                    <TextField
                        fullWidth
                        label="URL Dreamborn (opcjonalny)"
                        value={formData.dreamborn_url}
                        onChange={onInputChange('dreamborn_url')}
                        placeholder="https://dreamborn.ink/deck/..."
                        sx={inputSx}
                    />

                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, gap: 1.5 }}>
                <Button onClick={onClose}>
                    Anuluj
                </Button>
                <Button
                    onClick={onSubmit}
                    variant="contained"
                    disabled={!formData.name || !formData.archetype || !formData.cards}
                    sx={{
                        background: 'linear-gradient(135deg, #7c3aed, #3b82f6)',
                        borderRadius: '12px',
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #6d28d9, #2563eb)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(124, 58, 237, 0.4)'
                        },
                        '&:disabled': {
                            background: 'rgba(76, 29, 149, 0.3)',
                            color: 'rgba(196, 181, 253, 0.5)'
                        }
                    }}
                >
                    {editingDeck ? 'Zapisz Zmiany' : 'Dodaj Talię'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeckForm;
